import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext.js';

type ProtectedRouteProps = {
  children: ReactNode;
  /** If specified, only these roles can access the route. */
  allowedRoles?: ReadonlyArray<string>;
};

/**
 * Blocks access to the authenticated admin shell until a staff token is
 * present. Reads from the AuthContext (W0.8) so login/logout state
 * changes propagate without a manual re-render. Preserves origin path
 * in `state.from` for post-login bounce-back. Optional `allowedRoles`
 * gate matches HTHD's pattern — non-matching roles redirect to /,
 * letting the AppShell render whatever is appropriate for that role.
 *
 * Renders a minimal full-screen loader while AuthContext hydrates from
 * localStorage so a flash of /login doesn't appear when the user
 * actually has a valid session.
 */
export function ProtectedRoute({
  children,
  allowedRoles,
}: ProtectedRouteProps): JSX.Element {
  const location = useLocation();
  const { isAuthenticated, isLoading, staff } = useAuth();

  if (isLoading) {
    return (
      <div className="ferro-loading-screen" role="status" aria-live="polite">
        <div className="ferro-loading-screen__spinner" aria-hidden="true" />
        <p className="ferro-loading-screen__text">Loading…</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  if (allowedRoles && staff && !allowedRoles.includes(staff.role)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
